import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AbstractPageStateService } from '../../abstracts/services/onboarding/page-state.abstract';
import { IApiResponse } from '../../interfaces/api/api-response.interface';
import { ITransition } from '../../interfaces/onboarding/page-state/transition.interface';
import { PageState } from '../../models/page-state/page-state.model';
import { LoggerService } from '../common/logger.service';

@Injectable({
  providedIn: 'root',
})
export class PageStateService extends AbstractPageStateService {
  private pageStates: PageState[] = [];
  private apiUrl = environment.apiUrl;
  private navigationControllerSuffix = 'Onboarding/Onboarding/navigation/';
  private pageState: PageState;
  constructor(
    private http: HttpClient,
    private router: Router,
    private logger: LoggerService
  ) {
    super();
  }

  /**
   * Get PageState document by ID
   */
  getPageStateById(stateId: string): PageState {
    return this.pageStates.filter((state: PageState) => {
      if (state.id.toString() === stateId) {
        // eslint-disable-next-line no-param-reassign
        state.pageUrl = this.router.createUrlTree([state.pageUrl]).toString();
        return state;
      }
      return false;
    })[0];
  }

  /**
   * Get All Transitions by StateID
   */
  async getPageTransitionsByStateId(stateId: string): Promise<ITransition[]> {
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-transition/state`;
    const body = {
      id: stateId,
    };
    const result = await firstValueFrom(
      this.http.post<IApiResponse>(url, body).pipe()
    );
    if (result === null || result.data === null) return [];
    const data = result.data as [];
    return data.map((transition: ITransition) => transition);
  }

  getPageStateByEventAndTransitions(
    eventEnum: number,
    transitions: ITransition[]
  ): PageState {
    const transition = transitions.filter((t) => t.eventId === eventEnum)[0];
    if (transition === undefined) return new PageState();
    if (transition.disabled) return new PageState();
    const nextStateId: string = transition.nextStateId.toString();
    if (nextStateId === undefined) return new PageState();

    const state = this.getPageStateById(nextStateId) ?? new PageState();
    state.disabled = transition.disabled;

    state.pageUrl = this.router.createUrlTree([state.pageUrl]).toString();
    return state ?? new PageState();
  }

  async getCurrentPageState(stateId: string): Promise<PageState> {
    const state = await this.getPageStateById(stateId);
    state.pageUrl = this.router.createUrlTree([state.pageUrl]).toString();
    return state;
  }

  async loadPageStates() {
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-state`;
    const result = await firstValueFrom(
      this.http.get<IApiResponse>(url).pipe()
    );
    if (result === null || result.data === null) this.pageStates = [];
    else this.pageStates = (result.data as []).map((data: PageState) => data);
  }

  async loadPageStatesById(id: string): Promise<PageState> {
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-state`;
    const body = {
      id,
    };
    const result = await firstValueFrom(
      this.http.post<IApiResponse>(url, body).pipe()
    );
    if (result === null || result.data === null) return this.pageState;

    this.pageState = result.data as PageState;
    return this.pageState;
  }

  async getPageState_ClientAdditionalInfoRequestDashboard(): Promise<PageState> {
    const url = `${this.apiUrl}${this.navigationControllerSuffix}page-state/client-additional-info-request-dashboard`;
    const result = await firstValueFrom(this.http.get<IApiResponse>(url));
    return result.data as PageState;
  }
}
